import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetAppState } from "../shared/actions";
import { RootState } from "../storeConfig";

export enum WorkflowStage {
  UNKNOWN = "UNKNOWN",
  ORDER_OVERVIEW = "ORDER_OVERVIEW",
  RESTOCK_CANCELLED_ITEMS = "RESTOCK_CANCELLED_ITEMS",
  FORCED_RESTOCK = "FORCED_RESTOCK",
}

export interface WorkflowState {
  workflowStage: WorkflowStage;
}

type ForcedAction = {
  dismissible: boolean;
  title: string;
  description: string;
  type: "success" | "warning";
};

export interface GlobalState extends WorkflowState {
  forcedActions: ForcedAction[];
}

const initialState: GlobalState = {
  forcedActions: [],
  workflowStage: WorkflowStage.UNKNOWN,
};

const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    addForcedAction: (state, action: PayloadAction<ForcedAction>) => {
      state.forcedActions.push(action.payload);
    },

    popForcedAction: (state) => {
      state.forcedActions.shift();
    },

    // WORKFLOW STAGE
    triggerRestockCancelledItemsStage(state) {
      state.workflowStage = WorkflowStage.RESTOCK_CANCELLED_ITEMS;
    },
    triggerOrderOverviewStage(state) {
      state.workflowStage = WorkflowStage.ORDER_OVERVIEW;
    },
    triggerForcedRestockStage(state) {
      state.workflowStage = WorkflowStage.FORCED_RESTOCK;
    },
    triggerResetStage(state) {
      state.workflowStage = WorkflowStage.UNKNOWN;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(resetAppState, () => initialState);
  },
});

export const {
  addForcedAction,
  popForcedAction,
  triggerRestockCancelledItemsStage,
  triggerForcedRestockStage,
  triggerOrderOverviewStage,
  triggerResetStage,
} = globalSlice.actions;

const isMatchingWorkflowStage = (s: RootState, stage: WorkflowStage) =>
  s.globalStateReducer.workflowStage === stage;

// workflow stage selectors
export const currentWorkflowStage = (s: RootState) => s.globalStateReducer.workflowStage;
export const isRestockCancelledItemsStageSelector = (s: RootState) =>
  isMatchingWorkflowStage(s, WorkflowStage.RESTOCK_CANCELLED_ITEMS);
export const isForcedRestockStageSelector = (s: RootState) =>
  isMatchingWorkflowStage(s, WorkflowStage.FORCED_RESTOCK);
export const isOrderOverviewStageSelector = (s: RootState) =>
  isMatchingWorkflowStage(s, WorkflowStage.RESTOCK_CANCELLED_ITEMS);

export default globalSlice.reducer;
